import React, { useState, useEffect } from 'react';
import { makeStyles } from 'utils/makeStylesAdapter';
import {
  Grid,
  Box,
  TextareaAutosize,
  Typography,
  IconButton,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
  Checkbox,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import {
  InputConsensus,
  KeyboardDatePicker,
  CurrencyTextField,
  ButtonConsensus,
  InstallmentTypeRadioGroup,
  ModalConfirm,
  InstallmentVariablePerPeriod,
  InstallmentFixedPerPeriod,
} from 'components';
import { Autocomplete } from '@mui/material';
import { toast } from 'react-toastify';
import { currencyFormat, getOnlyNumbers } from 'utils/currency';
import { colors } from 'utils/colorsSteps';
import { PENALTY_KINDS } from 'constants/agreement/penaltyKinds';
import { useParameterKeys } from 'hooks/useParameterKeys';
import { useCustomerConfig } from 'hooks/useCustomerConfig';
import { PARAMETER_KEYS } from 'constants/parameters/parameter-keys';
import { useSelector } from 'react-redux';
import { INSTALLMENT_TYPES } from 'constants/agreement/installmentTypes';
import DeleteIcon from '@mui/icons-material/Delete';
import { parcelsSum, validateParcels } from 'utils/parcels';
import moment from 'moment';
import { formatDateToISO8601 } from 'utils/date-time.utils';
import InstallmentPaidBySpecificDate from 'components/InstallmentPaidBySpecificDate';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import agreementService from 'services/agreement/agreementService';
import LazyAutocomplete from 'components/LazyAutocomplete';
import CircularProgress from '@mui/material/CircularProgress';

const useStyles = makeStyles(theme => ({
  containerForm: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: {
    margin: theme.spacing(2, 0, 2, 0),
  },
  containerCheck: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  containerParcels: {
    padding: theme.spacing(2),
    backgroundColor: '#ececec',
    borderRadius: '0.7rem',
    margin: theme.spacing(0, 0.8, 1, 0.8),
  },
}));

const CompensationToVictims = props => {
  const {
    setDisabledActionsPenaltyTable = {},
    selectedIndex = null,
    setSelectedIndex = {},
    victims,
    ressarciment,
    setRessarciment,
  } = props;

  const classes = useStyles();
  const context = 'COMPENSATION_TO_VICTIM';
  const { isMPDFT } = useParameterKeys();
  const { enablePenaltyTypes } = useCustomerConfig();

  const parameters = useSelector(state => state.parameters);

  const enableInstallmentTypes = parameters.find(
    param => param.key === PARAMETER_KEYS.CONFIG_ENABLE_INSTALLMENT_TYPES && param.value === 'Sim',
  );

  const [row, setRow] = useState(null);
  const [key, setKey] = useState(false);

  const [installmentType, setInstallmentType] = useState(
    row?.installmentType || INSTALLMENT_TYPES.COMPENSATION_TO_VICTIM.FIXED_PER_PERIOD,
  );

  const [openDeleteParcelModalConfirm, setOpenDeleteParcelModalConfirm] = useState(false);
  const [parcelToDelete, setParcelToDelete] = useState(null);

  const initialValues = {
    deliveryDeadline: '',
    dateVictimCanReceive: '',
    value: 0,
    estimatedValue: 0,
    description: isMPDFT ? null : '',
    type: null,
    penalty: isMPDFT
      ? PENALTY_KINDS.COMPENSATION_TO_VICTIM_MPDFT
      : PENALTY_KINDS.COMPENSATION_TO_VICTIM,
    victim: null,
    notification: {
      whatsapp: false,
      letter: false,
      email: false,
    },
    parcels: [],
    numberOfParcels: 1,
    periodicy: '',
    totalValue: 0,
    minimumValue: null,
    maximumValue: null,
    initialDate: null,
    paymentDeadline: null,
  };

  const [formState, setFormState] = useState({
    isValid: false,
    values: { ...initialValues },
    touched: {},
    errors: {},
  });

  const [isAddingParcel, setIsAddingParcel] = useState(false);
  const [newParcel, setNewParcel] = useState({
    paymentDeadline: null,
    value: 0,
    minimumValue: null,
    maximumValue: null,
  });

  const handleToggleAddParcel = () => {
    setIsAddingParcel(!isAddingParcel);
    setNewParcel({ inicialDate: '', hours: '' });
  };

  useEffect(() => {
    setFormState(formState => ({
      ...formState,
      values:
        selectedIndex !== null
          ? {
              ...ressarciment[selectedIndex.index],
              estimatedValue: ressarciment[selectedIndex.index].estimatedValue / 100,
            }
          : { ...initialValues },
    }));
    selectedIndex && setInstallmentType(ressarciment[selectedIndex.index]?.installmentType);
  }, [selectedIndex]);

  const handleChangeCurrency = event => {
    setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        [event.target.name]: getOnlyNumbers(event.target.value),
      },
    }));
  };

  const handleChange = event => {
    setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        [event.target.name]:
          event.target.type === 'checkbox' ? event.target.checked : event.target.value,
      },
      touched: {
        ...formState.touched,
        [event.target.name]: true,
      },
    }));
  };

  const handleInputKeyPress = event => {
    if (event.key === ',' || event.key === '.') {
      event.preventDefault();
    }
  };

  const handleChangeNotification = event => {
    setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        notification: {
          ...formState.values.notification,
          [event.target.name]:
            event.target.type === 'checkbox' ? event.target.checked : event.target.value,
        },
      },
      touched: {
        ...formState.touched,
        [event.target.name]: true,
      },
    }));
  };

  const handleClearFormStat = () => {
    setRow(null);
    if (selectedIndex !== null) {
      setDisabledActionsPenaltyTable(prevState => ({
        ...prevState,
        [selectedIndex.penaltiesIndex]: { ...prevState[selectedIndex.penaltiesIndex], edit: false },
      }));
      setSelectedIndex(null);
    }
    setFormState(formState => ({
      ...formState,
      values: { ...initialValues },
    }));
  };

  useEffect(() => {
    if (row) {
      setFormState(formState => ({
        ...formState,
        values: { ...row },
      }));
      setKey(!key);
    }
    enableInstallmentTypes &&
      setInstallmentType(
        row?.installmentType || INSTALLMENT_TYPES.COMPENSATION_TO_VICTIM.FIXED_PER_PERIOD,
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row]);

  const handleSubmit = event => {
    event.preventDefault();

    const {
      victim,
      deliveryDeadline,
      dateVictimCanReceive,
      description,
      estimatedValue,
      parcels,
      type,
    } = formState.values;
    const { email, letter, whatsapp } = formState.values.notification;

    if (enablePenaltyTypes && !type) {
      toast.warn('Selecione o tipo');
      return;
    }

    if (!victim) {
      toast.warn('Selecione a vítima');
      return;
    }

    if (
      !enableInstallmentTypes &&
      (!estimatedValue || estimatedValue === 0 || estimatedValue == '0')
    ) {
      toast.warn('É necessário inserir um valor');
      return;
    }

    if (!enableInstallmentTypes && (!deliveryDeadline || deliveryDeadline === '')) {
      toast.warn('É necessário informar agenda do investigado');
      return;
    }

    if (!enableInstallmentTypes && (!dateVictimCanReceive || dateVictimCanReceive === '')) {
      toast.warn('É necessário informar agenda da vítima');
      return;
    }

    if (!enableInstallmentTypes && !isMPDFT && description === '') {
      toast.warn('É necessário inserir uma descrição');
      return;
    }

    if (!email && !letter && !whatsapp && !isMPDFT) {
      toast.warn('Selecione uma forma de notificação');
      return;
    }

    if (enableInstallmentTypes && parcels.length === 0) {
      toast.warn('É necessário adicionar ao menos uma parcela');
      return;
    }

    if (!validateParcels(parcels, installmentType)) {
      return;
    }

    if (selectedIndex !== null) {
      let newArray = [...ressarciment];
      if (enableInstallmentTypes) {
        newArray[selectedIndex.index] = { ...formState.values, installmentType };
      } else {
        newArray[selectedIndex.index] = formState.values;
      }
      setRessarciment(newArray);
    } else {
      if (
        deliveryDeadline &&
        dateVictimCanReceive &&
        ressarciment.find(
          value =>
            value.deliveryDeadline === deliveryDeadline &&
            value.dateVictimCanReceive === dateVictimCanReceive,
        )
      ) {
        toast.warn('Datas já inseridas');
        return;
      }

      if (enableInstallmentTypes) {
        setRessarciment([...ressarciment, { ...formState.values, installmentType }]);
      } else {
        setRessarciment([...ressarciment, formState.values]);
      }
    }

    handleClearFormStat();
  };

  const handleToRead = () => {
    handleClearFormStat();
  };

  const handleChangeVictim = (_event, newValue) => {
    setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        victim: newValue,
      },
    }));
    setKey(!key);
  };

  const fetchTypes = async () => {
    const result = await agreementService.getPenaltyTypes();
    return {
      data: result.data.compensationToVictimTypes,
    };
  };

  const handleChangeType = (_event, newValue) => {
    setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        type: newValue || null,
      },
    }));
  };

  const handleAddParcel = () => {
    const { deliveryDeadline, estimatedValue, numberOfParcels } = formState.values;

    if (!estimatedValue || estimatedValue == '0') {
      toast.warn('Insira um valor');
      return;
    }
    if (!numberOfParcels) {
      toast.warn('Insira a quantidade de parcelas');
      return;
    }

    const newParcels = [];
    let date = deliveryDeadline && moment(deliveryDeadline);

    for (let i = 0; i < numberOfParcels; i++) {
      newParcels.push({
        date: date && formatDateToISO8601(date.clone().add(i, 'months')),
        value: estimatedValue,
      });
    }

    setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        parcels: newParcels,
        deliveryDeadline: '',
        estimatedValue: 0,
      },
    }));
  };

  const handleAddNewParcel = () => {
    const { paymentDeadline, estimatedValue, maximumValue, minimumValue } = newParcel;

    if (!paymentDeadline) {
      return toast.warn('Insira a data');
    }

    if (
      installmentType === INSTALLMENT_TYPES.COMPENSATION_TO_VICTIM.FIXED_PER_PERIOD &&
      (!estimatedValue || estimatedValue == '0')
    ) {
      return toast.warn('Insira um valor');
    }

    let date = paymentDeadline && moment(paymentDeadline);

    const parcel = {
      date: date && formatDateToISO8601(date),
      value: estimatedValue,
      maximumValue,
      minimumValue,
    };

    setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        parcels: [...formState.values.parcels, parcel],
      },
    }));

    setIsAddingParcel(false);
  };

  const orderParcelsByDate = parcels => {
    return parcels.sort((a, b) => Date.parse(a.date) - Date.parse(b.date));
  };

  const handleParcelDateChange = (index, newDate, isInitial = false) => {
    const updatedParcels = formState.values.parcels.map((parcel, idx) =>
      idx === index
        ? isInitial
          ? { ...parcel, initialDate: newDate }
          : { ...parcel, date: newDate }
        : parcel,
    );
    setFormState({
      ...formState,
      values: {
        ...formState.values,
        parcels: updatedParcels,
      },
    });
  };

  const handleParcelValueChange = (index, event) => {
    const updatedParcels = formState.values.parcels.map((parcel, idx) =>
      idx === index
        ? { ...parcel, [event.target.name]: getOnlyNumbers(event.target.value) }
        : parcel,
    );
    setFormState({
      ...formState,
      values: {
        ...formState.values,
        parcels: updatedParcels,
      },
    });
  };

  const handleOpenDeleteParcelModalConfirm = parcel => {
    setOpenDeleteParcelModalConfirm(true);
    setParcelToDelete(parcel);
  };

  const deleteParcel = () => {
    const parcel = parcelToDelete;
    const index = formState.values.parcels.findIndex(item => item.date === parcel.date);
    if (index !== -1) {
      const newParcels = formState.values.parcels.filter(
        (value, _index, _arr) => value.date !== parcel.date,
      );
      setFormState(formState => ({
        ...formState,
        values: {
          ...formState.values,
          parcels: newParcels,
        },
      }));
    }
  };

  const handleCloseDeleteParcelModalConfirm = () => {
    setOpenDeleteParcelModalConfirm(false);
    setParcelToDelete(null);
  };

  return (
    <Box className={classes.container}>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={2} className={classes.containerForm}>
          <Grid size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
            {enablePenaltyTypes && (
              <LazyAutocomplete
                autoHighlight
                onChange={handleChangeType}
                value={formState.values.type}
                fetchOptions={fetchTypes}
                enablePagination
                renderInput={params => (
                  <InputConsensus
                    {...params}
                    label="Tipo*"
                    variant="standard"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {params.InputProps.loading ? (
                            <CircularProgress color="primary" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}
          </Grid>
          {!enableInstallmentTypes ? (
            <>
              <Grid size={{ xl: 6, lg: 6, md: 6, sm: 6, xs: 12 }}>
                <Autocomplete
                  options={victims}
                  fullWidth
                  autoHighlight
                  onChange={handleChangeVictim}
                  noOptionsText="Não encontrado"
                  value={formState.values.victim}
                  getOptionLabel={option => option?.name || ''}
                  renderInput={params => (
                    <InputConsensus
                      {...params}
                      name="victimId"
                      label="Vítima*"
                      variant="standard"
                      inputProps={{
                        ...params.inputProps,
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xl: 6, lg: 6, md: 6, sm: 6, xs: 12 }}>
                <CurrencyTextField
                  label="Valor*"
                  name="estimatedValue"
                  onChange={handleChangeCurrency}
                  value={formState.values.estimatedValue || 0}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xl: 6, lg: 6, md: 6, sm: 6, xs: 12 }}>
                <KeyboardDatePicker
                  variant="inline"
                  name="deliveryDeadline"
                  label="Agenda do investigado*"
                  value={formState.values.deliveryDeadline || null}
                  minDate={!selectedIndex ? new Date() : undefined}
                  onChange={newDate =>
                    setFormState(formState => ({
                      ...formState,
                      values: {
                        ...formState.values,
                        deliveryDeadline: newDate,
                      },
                    }))
                  }
                />
              </Grid>
              <Grid size={{ xl: 6, lg: 6, md: 6, sm: 6, xs: 12 }}>
                <KeyboardDatePicker
                  variant="inline"
                  name="dateVictimCanReceive"
                  label="Agenda da vítima*"
                  value={formState.values.dateVictimCanReceive || null}
                  minDate={formState.values.deliveryDeadline}
                  onChange={newDate =>
                    setFormState(formState => ({
                      ...formState,
                      values: {
                        ...formState.values,
                        dateVictimCanReceive: newDate,
                      },
                    }))
                  }
                />
              </Grid>
            </>
          ) : (
            <>
              <Grid size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
                <Autocomplete
                  options={victims}
                  fullWidth
                  autoHighlight
                  onChange={handleChangeVictim}
                  noOptionsText="Não encontrado"
                  value={formState.values.victim}
                  getOptionLabel={option => option?.name || ''}
                  renderInput={params => (
                    <InputConsensus
                      {...params}
                      name="victimId"
                      label="Vítima"
                      variant="standard"
                      inputProps={{
                        ...params.inputProps,
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid
                container
                spacing={2}
                className={classes.containerParcels}
                size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
                <Grid size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography align="left" variant="h5" className={classes.form}>
                      Parcelas
                    </Typography>
                    {formState.values.parcels.length > 0 &&
                      isMPDFT &&
                      !isAddingParcel &&
                      [
                        INSTALLMENT_TYPES.COMPENSATION_TO_VICTIM.FIXED_PER_PERIOD,
                        INSTALLMENT_TYPES.COMPENSATION_TO_VICTIM.VARIABLE_PER_PERIOD,
                      ].includes(installmentType) && (
                        <Tooltip title="Adicionar parcela" arrow placement="top">
                          <IconButton onClick={handleToggleAddParcel}>
                            <AddIcon style={{ color: colors.primary }} />
                          </IconButton>
                        </Tooltip>
                      )}
                  </Box>
                </Grid>

                {isAddingParcel && (
                  <>
                    <Grid size={{ xl: 3, lg: 3, md: 3, sm: 3, xs: 12 }}>
                      <KeyboardDatePicker
                        variant="inline"
                        name="newParcelDate"
                        label="Data"
                        value={newParcel.paymentDeadline || null}
                        onChange={newDate =>
                          setNewParcel({
                            ...newParcel,
                            paymentDeadline: newDate,
                          })
                        }
                      />
                    </Grid>
                    {installmentType ===
                      INSTALLMENT_TYPES.COMPENSATION_TO_VICTIM.FIXED_PER_PERIOD && (
                      <Grid size={{ xl: 4, lg: 4, md: 4, sm: 4, xs: 12 }}>
                        <CurrencyTextField
                          label="Valor *"
                          name="newParcelValue"
                          onChange={e =>
                            setNewParcel({
                              ...newParcel,
                              estimatedValue: getOnlyNumbers(e.target.value),
                            })
                          }
                          value={newParcel.value}
                          fullWidth
                        />
                      </Grid>
                    )}
                    {installmentType ===
                      INSTALLMENT_TYPES.COMPENSATION_TO_VICTIM.VARIABLE_PER_PERIOD && (
                      <>
                        <Grid size={{ xl: 3, lg: 3, md: 3, sm: 3, xs: 12 }}>
                          <CurrencyTextField
                            label="Valor Mínimo"
                            name="newParcelMinimumValue"
                            onChange={e =>
                              setNewParcel({
                                ...newParcel,
                                minimumValue: getOnlyNumbers(e.target.value),
                              })
                            }
                            value={newParcel.minimumValue}
                            allowNullValue={true}
                            fullWidth
                          />
                        </Grid>
                        <Grid size={{ xl: 3, lg: 3, md: 3, sm: 3, xs: 12 }}>
                          <CurrencyTextField
                            label="Valor Máximo"
                            name="newParcelMaximumValue"
                            onChange={e =>
                              setNewParcel({
                                ...newParcel,
                                maximumValue: getOnlyNumbers(e.target.value),
                              })
                            }
                            value={newParcel.maximumValue}
                            allowNullValue={true}
                            fullWidth
                          />
                        </Grid>
                      </>
                    )}
                    <Grid
                      style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '30px' }}
                      size={{ xl: 2, lg: 2, md: 2, sm: 2, xs: 12 }}>
                      <Tooltip title="Adicionar parcela" arrow placement="top">
                        <IconButton onClick={handleAddNewParcel}>
                          <AddIcon style={{ color: colors.primary }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancelar" arrow placement="top">
                        <IconButton onClick={handleToggleAddParcel} color="secondary">
                          <ClearIcon style={{ color: '#e1296a' }} />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  </>
                )}

                {formState.values.parcels.length === 0 && enableInstallmentTypes && (
                  <Grid size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
                    <InstallmentTypeRadioGroup
                      installmentType={installmentType}
                      setInstallmentType={setInstallmentType}
                      context={context}
                    />
                  </Grid>
                )}

                {enableInstallmentTypes ? (
                  <>
                    {installmentType ===
                      INSTALLMENT_TYPES.COMPENSATION_TO_VICTIM.FIXED_PER_PERIOD && (
                      <InstallmentFixedPerPeriod
                        formState={formState}
                        setFormState={setFormState}
                        handleChange={handleChange}
                        handleChangeValue={handleChangeCurrency}
                        handleInputKeyPress={handleInputKeyPress}
                        handleOpenDeleteParcelModalConfirm={handleOpenDeleteParcelModalConfirm}
                        handleParcelValueChange={handleParcelValueChange}
                        handleParcelDateChange={handleParcelDateChange}
                        selectedIndex={selectedIndex}
                        context={context}
                      />
                    )}
                    {installmentType ===
                      INSTALLMENT_TYPES.COMPENSATION_TO_VICTIM.VARIABLE_PER_PERIOD && (
                      <InstallmentVariablePerPeriod
                        formState={formState}
                        setFormState={setFormState}
                        handleChange={handleChange}
                        handleChangeCurrency={handleChangeCurrency}
                        handleInputKeyPress={handleInputKeyPress}
                        handleOpenDeleteParcelModalConfirm={handleOpenDeleteParcelModalConfirm}
                        handleParcelValueChange={handleParcelValueChange}
                        handleParcelDateChange={handleParcelDateChange}
                        selectedIndex={selectedIndex}
                        context={context}
                      />
                    )}
                    {installmentType ===
                      INSTALLMENT_TYPES.COMPENSATION_TO_VICTIM.PAID_BY_SPECIFIC_DATE && (
                      <InstallmentPaidBySpecificDate
                        formState={formState}
                        setFormState={setFormState}
                        handleChangeCurrency={handleChangeCurrency}
                        handleOpenDeleteParcelModalConfirm={handleOpenDeleteParcelModalConfirm}
                        handleParcelValueChange={handleParcelValueChange}
                        handleParcelDateChange={handleParcelDateChange}
                        selectedIndex={selectedIndex}
                        context={context}
                      />
                    )}
                  </>
                ) : (
                  <>
                    {formState.values.parcels.length === 0 && (
                      <>
                        <Grid size={{ xl: 5, lg: 5, md: 5, sm: 5, xs: 12 }}>
                          <KeyboardDatePicker
                            variant="inline"
                            name="deliveryDeadline"
                            label="Data inicial"
                            value={formState.values.deliveryDeadline || null}
                            minDate={!selectedIndex ? new Date() : undefined}
                            onChange={newDate =>
                              setFormState(formState => ({
                                ...formState,
                                values: {
                                  ...formState.values,
                                  deliveryDeadline: newDate,
                                },
                              }))
                            }
                          />
                        </Grid>
                        <Grid size={{ xl: 5, lg: 5, md: 5, sm: 5, xs: 12 }}>
                          <CurrencyTextField
                            label="Valor por parcela*"
                            name="estimatedValue"
                            onChange={handleChangeCurrency}
                            value={formState.values.estimatedValue || 0}
                            fullWidth
                          />
                        </Grid>
                      </>
                    )}
                    {formState.values.parcels && formState.values.parcels.length === 0 && (
                      <>
                        <Grid size={{ xl: 2, lg: 2, md: 2, sm: 2, xs: 12 }}>
                          <InputConsensus
                            label="Quantidade de parcelas*"
                            name="numberOfParcels"
                            variant="standard"
                            type="number"
                            inputProps={{
                              min: 1,
                            }}
                            onChange={handleChange}
                            onKeyPress={handleInputKeyPress}
                            value={formState.values.numberOfParcels || 1}
                            fullWidth
                          />
                        </Grid>
                        <Grid
                          style={{ display: 'flex', justifyContent: 'flex-end' }}
                          size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
                          <ButtonConsensus
                            title="Gerar Parcelas"
                            backgroundColor={colors.primary}
                            hovercolor={colors.primary}
                            onClick={() => handleAddParcel()}
                            size="medium"
                          />
                        </Grid>
                      </>
                    )}
                  </>
                )}
              </Grid>
              {!enableInstallmentTypes && (
                <Grid size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
                  <TableContainer component={Paper}>
                    <Table className={classes.table} size="small" aria-label="a dense table">
                      <TableHead>
                        <TableRow>
                          <TableCell align="center">Data</TableCell>
                          <TableCell align="center">Valor</TableCell>
                          <TableCell align="center" width={150}>
                            <Box display="flex" position="relative" justifyContent="center">
                              Ações
                            </Box>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {orderParcelsByDate(formState.values.parcels).map((item, index) => (
                          <TableRow key={index} hover>
                            <TableCell component="th" scope="row" align="center">
                              <KeyboardDatePicker
                                variant="inline"
                                name="deliveryDeadline"
                                value={item.date || null}
                                onChange={newDate => handleParcelDateChange(index, newDate)}
                              />
                            </TableCell>
                            <TableCell component="th" scope="row" align="center">
                              <CurrencyTextField
                                name="estimatedValue"
                                onKeyPress={handleInputKeyPress}
                                onChange={e => handleParcelValueChange(index, e)}
                                value={item.value / 100 || 0}
                                fullWidth
                              />
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={() => handleOpenDeleteParcelModalConfirm(item)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                      <TableFooter>
                        <TableRow>
                          <TableCell align="left" colSpan={3} style={{ fontWeight: 'bold' }}>
                            Total: {currencyFormat(parcelsSum(formState.values.parcels))}
                          </TableCell>
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </TableContainer>
                </Grid>
              )}
            </>
          )}
          <Grid size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
            <Box style={{ marginTop: 5, marginBottom: 5 }}>
              <Typography variant="h5">
                {isMPDFT ? 'Dados Bancários' : 'NOTIFICAR A VÍTIMA*'}
              </Typography>
            </Box>
            <TextareaAutosize
              style={{ width: '100%', padding: 5, resize: 'none' }}
              maxRows={4}
              minRows={4}
              aria-label="maximum height"
              placeholder={
                isMPDFT
                  ? ''
                  : 'Informar o valor ou descrever como será realizada a reparação à vítima realizado o ressarcimento'
              }
              onChange={handleChange}
              name="description"
              value={formState.values.description ?? ''}
            />
          </Grid>
          {!isMPDFT && (
            <Grid container size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
              <Grid size={{ xl: 4, lg: 4, md: 4, sm: 4, xs: 6 }}>
                <Box className={classes.containerCheck}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formState.values.notification.email}
                        onChange={handleChangeNotification}
                        name="email"
                        inputProps={{ 'aria-label': 'primary checkbox' }}
                      />
                    }
                    label="E-mail"
                  />
                </Box>
              </Grid>
              <Grid size={{ xl: 4, lg: 4, md: 4, sm: 4, xs: 6 }}>
                <Box className={classes.containerCheck}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formState.values.notification.letter}
                        onChange={handleChangeNotification}
                        name="letter"
                        inputProps={{ 'aria-label': 'primary checkbox' }}
                      />
                    }
                    label="Carta Registrada"
                  />
                </Box>
              </Grid>
              <Grid size={{ xl: 4, lg: 4, md: 4, sm: 4, xs: 6 }}>
                <Box className={classes.containerCheck}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formState.values.notification.whatsapp}
                        onChange={handleChangeNotification}
                        name="whatsapp"
                        inputProps={{ 'aria-label': 'primary checkbox' }}
                      />
                    }
                    label="Whatsapp"
                  />
                </Box>
              </Grid>
            </Grid>
          )}
          <Grid
            style={{ display: 'flex', justifyContent: 'flex-end' }}
            size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
            <ButtonConsensus
              title={`${selectedIndex !== null ? 'Alterar' : 'Incluir'} ${
                isMPDFT ? 'Medida' : 'Penalidade'
              }`}
              backgroundColor={colors.primary}
              hovercolor={colors.primary}
              type="submit"
              size="medium"
            />
            <ButtonConsensus
              title={`${selectedIndex !== null ? 'Cancelar' : 'Limpar'}`}
              backgroundColor={colors.primary}
              hovercolor={colors.primary}
              onClick={() => handleToRead()}
              size="medium"
              style={{ marginLeft: '-6px' }}
            />
          </Grid>
        </Grid>
      </form>
      {openDeleteParcelModalConfirm && (
        <ModalConfirm
          open={openDeleteParcelModalConfirm}
          handleClose={handleCloseDeleteParcelModalConfirm}
          onClick={deleteParcel}
          title="Excluir parcela"
        />
      )}
    </Box>
  );
};

export default CompensationToVictims;
